import React from 'react';
import PropTypes from 'prop-types';

import {Box, Typography, Stack, Card, Divider} from 'views/components';
import {AiCall, Translate} from 'views/module';
import {CheckCircle} from 'views/icons';

const AGENT_ID = process.env.REACT_APP_ELEVENLAB_AGENT_ID;

const InterviewSection = (props) => {
  const {caregiverData = {}} = props;

  const [connected, setConnected] = React.useState(false);
  const [done, setDone] = React.useState(false);

  const onCallStart = () => {
    setConnected(true);
  };

  const onCallEnd = () => {
    setConnected(false);
    setDone(true);
  };
  // Client tools for the AI agent to call during the conversation
  const clientTools = {
    get_initial_requirements: async () => {
      const result = {
        shifts: {
          instructions: 'Ask if they are currently working and the hours they are currently working.',
          should_ask: true,
        },
        specific_needs: {
          instructions: 'Ask about specific client care needs they can support. After their response if they don\'t mention one of the care' +
          ' needs in value ask them about the specific need missed.',
          value: caregiverData.specificNeeds,
          should_ask: (caregiverData.specificNeeds) !== 'none',
        },
      };
      return result;
    },
    get_qualification_questions: async () => {
      const result = {
        hourly_rate: {
          instructions: 'Ask for their preferred hourly rate. If they ask for a higher rate than offered, ask if they are willing to accept the offered rate.' +
            'Otherwise move on to the next question.',
          role_rate: caregiverData.interviewQuestions.hourlyRate || 'none',
          should_ask: caregiverData.interviewQuestions.hourlyRate !== 'none',
        },
        languages: {
          instructions: 'Confirm they speak the required language. At the start tell them you will ask them questions in the required languages. ' +
            'When asking the following question you must switch to the language in required_languages. Ask them about their fluency, they must ' +
            'respond in the language asked.',
          required_languages: caregiverData.interviewQuestions.languages || 'none',
          should_ask: caregiverData.interviewQuestions.languages !== 'none',
        },
        certificates: {
          instructions: 'Ask them what certifications they have. Ask them to list all relevant certificates.',
          should_ask: (
            (caregiverData.interviewQuestions.certifications.required) !== 'none' ||
            (caregiverData.interviewQuestions.certifications.atLeastOne) !== 'none'
          ),
        },
        start_date_availability: {
          instructions: 'Ask them for their earliest possible start date.',
          required_start_date: caregiverData.interviewQuestions.startDate || 'none',
          should_ask: caregiverData.interviewQuestions.startDate !== 'none',
        },
        considerations: {
          instructions: 'Ask questions based on the following considerations the client has provided.',
          questions: caregiverData.interviewQuestions.considerations || 'none',
          should_ask: caregiverData.interviewQuestions.considerations !== 'none',
        },
      };
      return result;
    },
    get_motivation_questions: async () => {
      const result = {
        motivation: {
          instructions: 'Ask the following motivation questions one by one.',
          should_ask: true,
          questions: [
            'Years of experience in a professional setting',
            'What draws them to caregiving',
            'Their favorite caregiving experience',
            'How they handled a challenging client situation',
          ],
        },
      };
      return result;
    },
  };

  const dynamicVariables = {
    'first_name': caregiverData.contact.firstName || 'there',
    'job_rn': caregiverData.jobRn || 'none',
    'secret_caregiver_data': JSON.stringify(caregiverData),
  };

  return (
    <Box
      sx={{py: 3, px: {lg: 18, md: 5, xs: 1}}}
    >
      <Typography variant="h5" gutterBottom align="center" fontWeight={600}>
        <Translate expression='caregiverInterview.Interview.Title'/>
      </Typography>
      <Typography variant="body2" color="text.secondary" align="center" sx={{mb: 3}}>
        <Translate expression='caregiverInterview.Interview.Subtitle'/>
      </Typography>

      <Stack spacing={3} alignItems="center">
        {/* Status Display */}
        <Card sx={{width: '100%', maxWidth: 500, p: 3}}>
          <Stack spacing={2} alignItems="center">

            <AiCall
              agentId={AGENT_ID}
              dynamicVariables={dynamicVariables}
              clientTools={clientTools}

              onCallStart={onCallStart}
              onCallEnd={onCallEnd}
            />
            <Divider sx={{width: '100%'}} />

            {/* Interview Instructions */}
            {
              !connected && !done && (
                <Box sx={{textAlign: 'center'}}>
                  <Typography variant="body1" gutterBottom>
                    <Translate expression='caregiverInterview.Interview.ReadyToStart.Title'/>
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{mb: 2}}>
                    <Translate expression='caregiverInterview.Interview.ReadyToStart.Instructions'/>
                  </Typography>
                </Box>
              )
            }

            {/* Active Call Controls */}
            {
              connected && (
                <Box sx={{textAlign: 'center'}}>
                  <Typography variant="body1" gutterBottom>
                    <Translate expression='caregiverInterview.Interview.InProgress.Title'/>
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{mb: 3}}>
                    <Translate expression='caregiverInterview.Interview.InProgress.Instructions'/>
                  </Typography>


                </Box>
              )
            }

            {/* Completed State */}
            {
              done && (
                <Box sx={{textAlign: 'center'}}>
                  <CheckCircle color="success" sx={{fontSize: 48, mb: 2}} />
                  <Typography variant="h6" gutterBottom color="success.main">
                    <Translate expression='caregiverInterview.Interview.Completed.Title'/>
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    <Translate expression='caregiverInterview.Interview.Completed.Message'/>
                  </Typography>
                </Box>
              )
            }
          </Stack>
        </Card>

        {/* Interview Guidelines */}
        {
          !connected && !done && (
            <Card sx={{width: '100%', maxWidth: 500, p: 3, bgcolor: 'background.paper'}}>
              <Typography variant="subtitle1" gutterBottom fontWeight={600}>
                <Translate expression='caregiverInterview.Interview.Guidelines.Title'/>
              </Typography>
              <Stack spacing={1}>
                <Typography variant="body2" color="text.secondary">
                  <Translate expression='caregiverInterview.Interview.Guidelines.QuietEnvironment'/>
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <Translate expression='caregiverInterview.Interview.Guidelines.SpeakClearly'/>
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <Translate expression='caregiverInterview.Interview.Guidelines.BeHonest'/>
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <Translate expression='caregiverInterview.Interview.Guidelines.CoverTopics'/>
                </Typography>
              </Stack>
            </Card>
          )
        }
      </Stack>
    </Box>
  );
};

InterviewSection.propTypes = {
  caregiverData: PropTypes.object,
};

export default InterviewSection;
